/**
 * Empire skirmish map pool — ~100 named layouts with seeded procedural terrain.
 * Original names only. Each map is balanced for 2–8 seats (practice uses 4).
 */

import { isCliffFace, MAP_HALF, riverAxis } from "./empire-terrain";

export { MAP_HALF };
export const WATER_CELL = 2;

export type MapBiome =
  | "mainland"
  | "islands"
  | "river"
  | "coastal"
  | "lake"
  | "archipelago"
  | "forest"
  | "plains"
  | "ridge"
  | "fjord"
  | "twin_seas"
  | "crater";

export type EmpireMapDef = {
  id: string;
  name: string;
  biome: MapBiome;
  /** Stable seed for deterministic regen */
  seed: number;
  blurb: string;
};

/** Mulberry32 — fast deterministic PRNG from a 32-bit seed. */
export function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export function cellWet(grid: Record<string, 1> | undefined, x: number, z: number): boolean {
  return !!grid?.[waterKey(x, z)];
}

function waterKey(x: number, z: number): string {
  const ix = Math.floor((x + MAP_HALF) / WATER_CELL);
  const iz = Math.floor((z + MAP_HALF) / WATER_CELL);
  return `${ix},${iz}`;
}

const BIOME_NAMES: Record<MapBiome, string[]> = {
  mainland: [
    "Amber Expanse",
    "Crown Lowlands",
    "Herald Plains",
    "Stone March",
    "Banner Reach",
    "Iron Meadows",
    "Old Cart Road",
    "Harvest Fold",
    "King's Pasture",
    "Quiet Marches",
  ],
  islands: [
    "Three Lanterns",
    "Sparrow Keys",
    "Driftwood Chain",
    "Coral Steps",
    "Pilot's Nest",
    "Saltcrown Isles",
    "Far Beacon",
    "Gull Ring",
    "Tideward Keys",
    "Anchor Scatter",
  ],
  river: [
    "Serpent Ford",
    "Twin Banks",
    "Millrace Divide",
    "Crosswater",
    "Reed Split",
    "Ferry Bend",
    "Longford Vein",
    "Oxbow Run",
    "Silver Channel",
    "Bridge of Oaths",
  ],
  coastal: [
    "Windward Shore",
    "Harbor Crescent",
    "Cliffmarket Bay",
    "Dune Gate",
    "Kelp Coast",
    "Breaker Point",
    "Sailor's Shelf",
    "Mist Pier",
    "Cape of Embers",
    "Low Tide Road",
  ],
  lake: [
    "Mirror Basin",
    "Central Mere",
    "Heron Bowl",
    "Deepglass Lake",
    "Stillwater Crown",
    "Otter Ring",
    "Moonpool",
    "Crane Mere",
    "Widewater",
    "Basin of Banners",
  ],
  archipelago: [
    "Hundred Sails",
    "Shard Sea",
    "Pearl Ladder",
    "Storm Necklace",
    "Isle Ladder",
    "Broken Horizon",
    "Lantern Archipelago",
    "Wavegrid",
    "Salt Maze",
    "Far Ring Chain",
  ],
  forest: [
    "Deep Timber",
    "Green Throat",
    "Wolf Copse",
    "Cedar Wall",
    "Moss Road",
    "Oak Labyrinth",
    "Whisper Grove",
    "Dark Canopy",
    "Pine Barricade",
    "Hunter's Vein",
  ],
  plains: [
    "Open Standard",
    "Wide Banner",
    "Skyfield",
    "Dust Circuit",
    "Empty Horn",
    "Long Sight",
    "Flat Crown",
    "Steppe Gate",
    "Gale Flats",
    "Swift March",
  ],
  ridge: [
    "Spine of Stone",
    "Knife Ridge",
    "Broken Back",
    "Granite Wall",
    "Pass of Embers",
    "High Divide",
    "Crag Ladder",
    "Saddle Ridge",
    "Iron Spine",
    "Watcher's Crest",
  ],
  fjord: [
    "Narrow Fathom",
    "Cold Inlet",
    "Blackwater Fjord",
    "Claw Sound",
    "Icevein Arm",
    "Deep Cut Bay",
    "Horn Inlet",
    "Shadow Sound",
    "Rift Harbor",
    "Needle Fjord",
  ],
  twin_seas: [
    "Two Oceans",
    "Split Tideline",
    "Double Horizon",
    "East-West Waters",
    "Paired Gulfs",
    "Mirror Seaways",
    "Twin Havens",
    "Dual Coast War",
    "Bilateral Tide",
    "Opposite Shores",
  ],
  crater: [
    "Sunken Crown",
    "Ash Bowl",
    "Impact Mere",
    "Crater Ring",
    "Fallen Star Basin",
    "Hollow Shield",
    "Rim Fortress",
    "Caldera Gate",
    "Scorched Bowl",
    "Ring of Stone",
  ],
};

const BIOME_BLURB: Record<MapBiome, string> = {
  mainland: "Open land, a river, and fords you can walk",
  islands: "Four home islands, causeways, and a center isle",
  river: "Two banks, a wide river, and three fords",
  coastal: "A continent with one ocean bay",
  lake: "A ring of land around a central lake",
  archipelago: "Home isles, small islets, and walkable links",
  forest: "Deep woods and a narrow fordable stream",
  plains: "Open grass and a few ponds",
  ridge: "High ground with one pass through the middle",
  fjord: "A northern inlet and land on both sides",
  twin_seas: "Seas north and south, a land bridge between",
  crater: "A ring of water with roads into the bowl",
};

const BIOME_ORDER: MapBiome[] = [
  "mainland",
  "islands",
  "river",
  "coastal",
  "lake",
  "archipelago",
  "forest",
  "plains",
  "ridge",
  "fjord",
  "twin_seas",
  "crater",
];

/** Exactly 100 playable skirmish maps. */
export const EMPIRE_MAP_POOL: EmpireMapDef[] = (() => {
  const out: EmpireMapDef[] = [];
  let n = 0;
  for (const biome of BIOME_ORDER) {
    const names = BIOME_NAMES[biome];
    for (let i = 0; i < names.length; i++) {
      if (out.length >= 100) break;
      const seed = (0x4e7a1e ^ (n * 2654435761) ^ (biome.length << 8) ^ (i * 9973)) >>> 0;
      out.push({
        id: `map_${String(n + 1).padStart(3, "0")}`,
        name: names[i],
        biome,
        seed,
        blurb: BIOME_BLURB[biome],
      });
      n += 1;
    }
  }
  // Pad to 100 if biome lists undershoot (shouldn't)
  while (out.length < 100) {
    const i = out.length;
    out.push({
      id: `map_${String(i + 1).padStart(3, "0")}`,
      name: `Frontier ${i + 1}`,
      biome: BIOME_ORDER[i % BIOME_ORDER.length],
      seed: (0xabc123 + i * 7919) >>> 0,
      blurb: "frontier skirmish",
    });
  }
  return out.slice(0, 100);
})();

export function getMapById(id: string | null | undefined): EmpireMapDef | null {
  if (!id) return null;
  return EMPIRE_MAP_POOL.find((m) => m.id === id) || null;
}

export function pickRandomMap(rng = Math.random): EmpireMapDef {
  const i = Math.floor(rng() * EMPIRE_MAP_POOL.length) % EMPIRE_MAP_POOL.length;
  return EMPIRE_MAP_POOL[i];
}

/** Two maps from every biome, so the picker always offers at least 20. */
export const MAP_PICK_LIST: EmpireMapDef[] = BIOME_ORDER.flatMap((biome) =>
  EMPIRE_MAP_POOL.filter((m) => m.biome === biome).slice(0, 2)
);

export const MAP_FACE: Record<MapBiome, string> = {
  mainland: "Land, river to the sea, hills, forest",
  islands: "Ocean, islands, beaches, trees",
  river: "River valley, fords, woods",
  coastal: "Ocean coast, shore, hills",
  lake: "Lake, river outlet, mountains",
  archipelago: "Open ocean and many islands",
  forest: "Deep forest, river, hills",
  plains: "Open land, river, light woods",
  ridge: "Mountains, passes, river",
  fjord: "Cliffs, inlet, forest",
  twin_seas: "Two oceans and a land bridge",
  crater: "Mountain bowl and a lake",
};

export type GeneratedTerrain = {
  water: Record<string, 1>;
  beaches: Record<string, 1>;
  /** Suggested resource spawn hubs (land). */
  resourceHubs: Array<{ x: number; z: number; kind: "wood" | "gold" | "stone" | "food" }>;
  /** Island centers for landing / secondary bases. */
  islands: Array<{ x: number; z: number; r: number }>;
};

type SpawnPad = { x: number; z: number };

function eraseDisk(
  water: Record<string, 1>,
  cx: number,
  cz: number,
  r: number
) {
  for (let x = cx - r; x <= cx + r; x += WATER_CELL) {
    for (let z = cz - r; z <= cz + r; z += WATER_CELL) {
      if ((x - cx) ** 2 + (z - cz) ** 2 <= r * r) {
        delete water[waterKey(x, z)];
      }
    }
  }
}

function fillLake(
  water: Record<string, 1>,
  cx: number,
  cz: number,
  rx: number,
  rz: number,
  rot: number,
  rand: () => number
) {
  const cos = Math.cos(rot);
  const sin = Math.sin(rot);
  const pad = Math.max(rx, rz) + 1.2;
  const step = WATER_CELL * 0.35;
  for (let x = cx - pad; x <= cx + pad; x += step) {
    for (let z = cz - pad; z <= cz + pad; z += step) {
      const dx = x - cx;
      const dz = z - cz;
      const lx = dx * cos + dz * sin;
      const lz = -dx * sin + dz * cos;
      const n =
        1 +
        0.1 *
          (Math.sin(lx * 0.55 + cz * 0.1 + rand() * 0.01) +
            Math.cos(lz * 0.7 - cx * 0.08));
      if ((lx * lx) / (rx * n) ** 2 + (lz * lz) / (rz * n) ** 2 <= 1) {
        if (Math.abs(x) <= MAP_HALF && Math.abs(z) <= MAP_HALF) {
          water[waterKey(x, z)] = 1;
        }
      }
    }
  }
}

function dryRoad(
  water: Record<string, 1>,
  x0: number,
  z0: number,
  x1: number,
  z1: number,
  half: number
) {
  const dist = Math.hypot(x1 - x0, z1 - z0) || 1;
  const steps = Math.ceil(dist / (half * 0.8));
  for (let i = 0; i <= steps; i++) {
    const t = i / steps;
    eraseDisk(water, x0 + (x1 - x0) * t, z0 + (z1 - z0) * t, half);
  }
}

/** Towns sit on big dry plazas. Roads reach the middle so nobody is trapped. */
function linkHomes(water: Record<string, 1>, pads: SpawnPad[], plaza: number, road: number) {
  for (const p of pads) {
    eraseDisk(water, p.x, p.z, plaza);
    dryRoad(water, p.x, p.z, 0, 0, road);
  }
  eraseDisk(water, 0, 0, 16);
}

/** Lake and crater maps walk a ring, then a spoke into the middle where it is dry. */
function linkRing(water: Record<string, 1>, pads: SpawnPad[], radius: number, plaza: number, road: number) {
  for (const p of pads) {
    eraseDisk(water, p.x, p.z, plaza);
    const ang = Math.atan2(p.z, p.x);
    dryRoad(water, p.x, p.z, Math.cos(ang) * radius, Math.sin(ang) * radius, road);
  }
  for (let a = 0; a < Math.PI * 2; a += 0.2) {
    eraseDisk(water, Math.cos(a) * radius, Math.sin(a) * radius, road);
  }
}

function paintCell(water: Record<string, 1>, x: number, z: number) {
  if (Math.abs(x) > MAP_HALF || Math.abs(z) > MAP_HALF) return;
  water[waterKey(x, z)] = 1;
}

function fillOcean(water: Record<string, 1>) {
  for (let x = -MAP_HALF; x <= MAP_HALF; x += WATER_CELL) {
    for (let z = -MAP_HALF; z <= MAP_HALF; z += WATER_CELL) paintCell(water, x, z);
  }
}

function beachRing(
  water: Record<string, 1>,
  beaches: Record<string, 1>,
  cx: number,
  cz: number,
  r: number
) {
  for (let a = 0; a < Math.PI * 2; a += 0.2) {
    for (let dr = 0; dr < 1.6; dr += 0.4) {
      const x = cx + Math.cos(a) * (r + dr);
      const z = cz + Math.sin(a) * (r + dr);
      if (Math.abs(x) > MAP_HALF || Math.abs(z) > MAP_HALF) continue;
      if (!water[waterKey(x, z)]) beaches[waterKey(x, z)] = 1;
    }
  }
}

/**
 * Build coherent terrain for a map definition + spawn pads.
 * Guarantees dry bases, usable water for ships where biome intends, proper islands.
 */
export function generateMapTerrain(
  def: EmpireMapDef,
  pads: SpawnPad[]
): GeneratedTerrain {
  const rand = mulberry32(def.seed);
  const water: Record<string, 1> = {};
  const beaches: Record<string, 1> = {};
  const islands: Array<{ x: number; z: number; r: number }> = [];
  const resourceHubs: GeneratedTerrain["resourceHubs"] = [];

  const biome = def.biome;

  // Water first. Roads are carved last so every town can walk to the middle.
  if (biome === "islands" || biome === "archipelago") {
    fillOcean(water);
    if (biome === "archipelago") {
      for (let i = 0; i < 6; i++) {
        const a = rand() * Math.PI * 2;
        const dist = 28 + rand() * 30;
        const ix = Math.cos(a) * dist;
        const iz = Math.sin(a) * dist;
        const r = 5 + rand() * 3;
        eraseDisk(water, ix, iz, r);
        islands.push({ x: ix, z: iz, r });
        beachRing(water, beaches, ix, iz, r);
      }
    }
  } else if (biome === "twin_seas") {
    for (let x = -MAP_HALF; x <= MAP_HALF; x += WATER_CELL) {
      for (let z = -MAP_HALF; z <= MAP_HALF; z += WATER_CELL) {
        if (z > 58 || z < -58) paintCell(water, x, z);
      }
    }
  } else if (biome === "coastal") {
    const side = def.seed % 2;
    for (let x = -MAP_HALF; x <= MAP_HALF; x += WATER_CELL) {
      for (let z = -MAP_HALF; z <= MAP_HALF; z += WATER_CELL) {
        const shore = 36 + Math.sin((side ? z : x) * 0.05) * 8;
        const wet = side === 0 ? x > shore && Math.abs(z) < 62 : z > shore && Math.abs(x) < 62;
        if (wet) paintCell(water, x, z);
      }
    }
  } else if (biome === "lake") {
    fillLake(water, 0, 0, 26, 20, rand() * 0.6, rand);
  } else if (biome === "crater") {
    for (let x = -40; x <= 40; x += 1) {
      for (let z = -40; z <= 40; z += 1) {
        const d = Math.hypot(x, z);
        if (d > 18 && d < 34) paintCell(water, x, z);
      }
    }
  } else if (biome === "fjord") {
    for (let z = 16; z <= MAP_HALF; z += 1) {
      const half = 8 + Math.sin(z * 0.08) * 4;
      for (let x = -half; x <= half; x += 1) paintCell(water, x, z);
    }
  } else if (biome === "plains") {
    fillLake(water, -20, 16, 10, 7, 0.4, rand);
    fillLake(water, 24, -18, 8, 6, 0.2, rand);
  } else if (biome === "ridge") {
    for (let x = -MAP_HALF; x <= MAP_HALF; x += 1) {
      if (Math.abs(x) < 8) continue;
      const z = Math.sin(x * 0.07) * 10;
      for (let w = -5; w <= 5; w += 1) paintCell(water, x, z + w);
    }
  } else {
    // Mainland, river, forest: a river with three fords.
    const wide = biome === "river" ? 11 : biome === "forest" ? 5 : 7;
    const fords = [-32, 0, 34];
    for (let z = -MAP_HALF; z <= MAP_HALF; z += 1) {
      if (fords.some((f) => Math.abs(z - f) < 6)) continue;
      const wobble = Math.sin(z * 0.045 + def.seed * 0.0001) * 8;
      for (let w = -wide; w <= wide; w += 1) paintCell(water, wobble + w, z);
    }
  }

  if (biome === "lake" || biome === "crater") linkRing(water, pads, biome === "crater" ? 42 : 40, 24, 6);
  else linkHomes(water, pads, biome === "islands" || biome === "archipelago" ? 26 : 22, 6);

  for (const p of pads) islands.push({ x: p.x, z: p.z, r: 22 });
  islands.push({ x: 0, z: 0, r: 14 });
  for (const isle of islands) beachRing(water, beaches, isle.x, isle.z, isle.r * 0.8);

  // Fair resource hubs near each base (not on water)
  for (const p of pads) {
    const face = Math.atan2(-p.z, -p.x);
    const hx = p.x + Math.cos(face) * 7;
    const hz = p.z + Math.sin(face) * 7;
    resourceHubs.push({ x: hx, z: hz, kind: "wood" });
    resourceHubs.push({
      x: p.x + Math.cos(face + 1.1) * 6,
      z: p.z + Math.sin(face + 1.1) * 6,
      kind: "gold",
    });
    resourceHubs.push({
      x: p.x + Math.cos(face - 1.1) * 6,
      z: p.z + Math.sin(face - 1.1) * 6,
      kind: "stone",
    });
    resourceHubs.push({
      x: p.x + Math.cos(face + 0.4) * 5,
      z: p.z + Math.sin(face + 0.4) * 5,
      kind: "food",
    });
  }

  // Mid-map contested resources — offset from the river so the center stays a fight
  resourceHubs.push({ x: 16, z: 20, kind: "gold" });
  resourceHubs.push({ x: -18, z: -14, kind: "stone" });
  for (const side of [1, -1]) {
    for (let i = 0; i < 4; i++) {
      const t = -0.65 + (i / 3) * 1.3;
      const x = t * 48;
      const axis = riverAxis(def.seed, x);
      resourceHubs.push({
        x,
        z: axis.z + side * (8 + (i % 2) * 2),
        kind: "wood",
      });
    }
  }

  const settled: GeneratedTerrain["resourceHubs"] = [];
  for (const h of resourceHubs) {
    const spot = settleHub(h, water, def.seed, biome);
    if (spot) settled.push(spot);
  }

  return { water, beaches, resourceHubs: settled, islands };
}

function settleHub(
  h: GeneratedTerrain["resourceHubs"][number],
  water: Record<string, 1>,
  seed: number,
  biome: string
): GeneratedTerrain["resourceHubs"][number] | null {
  for (let ring = 0; ring <= 5; ring++) {
    const steps = ring === 0 ? 1 : 8;
    for (let k = 0; k < steps; k++) {
      const a = (k / steps) * Math.PI * 2;
      const x = h.x + Math.cos(a) * ring;
      const z = h.z + Math.sin(a) * ring;
      if (Math.abs(x) > MAP_HALF - 1 || Math.abs(z) > MAP_HALF - 1) continue;
      if (water[waterKey(x, z)]) continue;
      if (isCliffFace(x, z, seed, biome)) continue;
      return { x, z, kind: h.kind };
    }
  }
  return null;
}
